import { Fragment, useMemo, useState } from 'react';
import { Card, CardHeader } from '../../../components/ui/Card';
import { EmptyState } from '../../../components/ui/States';
import { Select } from '../../../components/ui/Input';
import { Nil, Table, TableFooter, TGroupRow, THead, Th, Td, Tr } from '../../../components/ui/Table';
import type { StaleDeal } from '../../../api/projectManagement';
import { formatMoneyCompact } from '../../../utils/numberFormat';
import { SALES_REPS } from '../../ProjectManagement/team';
import { OPEN_STAGES_ORDERED, formatStage, plural } from '../../ProjectManagement/formatters';
import { ATTIO_GO_LIVE_ISO } from '../../ProjectManagement/Sales/dealKinds';
import { AttioLink, RepInitials, formatShortDate, humanizeDealName, majorityOwner } from '../shared';
import { CompanySubLine, formatCloseDate, isPlaceholderCloseDate } from './PipelineOverview';

/** Rows shown per stage before "Show all"; keeps a 100-deal list scannable. */
const GROUP_PREVIEW = 8;

/** Whole days between the Attio go-live and now — a deal at this age has not moved since the import. */
function daysSinceGoLive(): number {
  return Math.floor((Date.now() - new Date(ATTIO_GO_LIVE_ISO).getTime()) / 86_400_000);
}

const stageRank = (stage: string) => {
  const i = (OPEN_STAGES_ORDERED as readonly string[]).indexOf(stage);
  return i === -1 ? OPEN_STAGES_ORDERED.length : i;
};

/**
 * Pipeline → stale deals: open deals with no stage change AND no email or
 * meeting on the account in staleAfterDays+ days — to move, close or re-date
 * in Attio, grouped by stage. As of today, not tied to a period.
 */
export function StaleDealsSection({
  deals,
  staleAfterDays,
  totalValueEur,
}: {
  deals: StaleDeal[];
  staleAfterDays: number;
  totalValueEur: number;
}) {
  const [rep, setRep] = useState<string>('all');
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const shown = useMemo(() => (rep === 'all' ? deals : deals.filter((d) => (d.repSlug ?? 'other') === rep)), [deals, rep]);

  const groups = useMemo(() => {
    const byStage = new Map<string, StaleDeal[]>();
    for (const d of shown) {
      const list = byStage.get(d.stage) ?? [];
      list.push(d);
      byStage.set(d.stage, list);
    }
    return [...byStage.entries()]
      .sort(([a], [b]) => stageRank(a) - stageRank(b))
      .map(([stage, list]) => ({
        stage,
        deals: [...list].sort((a, b) => b.valueEur - a.valueEur),
        value: list.reduce((s, d) => s + d.valueEur, 0),
      }));
  }, [shown]);

  const counts = new Map<string, number>();
  for (const d of deals) counts.set(d.repSlug ?? 'other', (counts.get(d.repSlug ?? 'other') ?? 0) + 1);
  // Days counted from the Attio go-live mean "not moved since the import", not a real age.
  const goLiveAge = daysSinceGoLive();
  const shownValue = shown.reduce((s, d) => s + d.valueEur, 0);
  // One owner on most rows is named once in the header; rows then only mark the other owners.
  const ownerKey = (d: StaleDeal) => d.repSlug ?? (d.ownerEmail ? `email:${d.ownerEmail}` : null);
  const usualOwner = majorityOwner(shown, ownerKey);
  const usualOwnerRep = usualOwner ? SALES_REPS.find((r) => r.slug === usualOwner) : undefined;
  const usualOwnerName = usualOwnerRep?.name ?? usualOwner?.replace(/^email:/, '').split('@')[0] ?? null;
  const ownerColumn = !(usualOwner && shown.every((d) => ownerKey(d) === usualOwner));
  const cols = ownerColumn ? 7 : 6;
  // Counted in the header, shown as "since go-live" on each such row (a repeated 278 reads as data).
  const goLiveCount = shown.filter((d) => d.daysSinceStageChange != null && d.daysSinceStageChange >= goLiveAge - 1).length;

  const toggle = (stage: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(stage)) next.delete(stage);
      else next.add(stage);
      return next;
    });

  return (
    <Card padding="md">
      <CardHeader
        title="Stale deals"
        description={`${plural(deals.length, 'deal')} · ${formatMoneyCompact(totalValueEur)} · no stage change and no email or meeting on the account in ${staleAfterDays}+ days${
          goLiveCount > 0 ? ` · ${goLiveCount} not moved since the Attio go-live (8 Jan 2026)` : ''
        }${usualOwnerName ? (ownerColumn ? ` · owner ${usualOwnerName} unless marked` : ` · all owned by ${usualOwnerName}`) : ''}`}
        action={
          <Select size="sm" value={rep} onChange={(e) => setRep(e.target.value)} aria-label="Owner">
            <option value="all">All owners · {deals.length}</option>
            {SALES_REPS.map((r) => (
              <option key={r.slug} value={r.slug}>
                {r.name} · {counts.get(r.slug) ?? 0}
              </option>
            ))}
            {(counts.get('other') ?? 0) > 0 && <option value="other">Unassigned · {counts.get('other')}</option>}
          </Select>
        }
      />
      {shown.length === 0 ? (
        <EmptyState title="No stale deals" compact />
      ) : (
        <>
          <Table minWidth={780}>
            <THead>
              <tr>
                <Th>Deal</Th>
                {ownerColumn && (
                  <Th align="center" title={usualOwnerName ? `Blank = ${usualOwnerName}` : undefined}>
                    {usualOwnerName ? 'Other owner' : 'Owner'}
                  </Th>
                )}
                <Th align="right">Days in stage</Th>
                <Th align="right" title="Latest email or meeting in Attio with the company or its people">
                  Last activity
                </Th>
                <Th align="right">Value</Th>
                <Th align="right" className="!pr-3">Close date</Th>
                <Th className="w-10 !pr-0">
                  <span className="sr-only">Attio</span>
                </Th>
              </tr>
            </THead>
            <tbody>
              {groups.map((g) => {
                const open = expanded.has(g.stage);
                const rows = open ? g.deals : g.deals.slice(0, GROUP_PREVIEW);
                const hidden = g.deals.length - rows.length;
                return (
                  <Fragment key={g.stage}>
                    <TGroupRow
                      colSpan={cols}
                      label={formatStage(g.stage)}
                      summary={`${plural(g.deals.length, 'deal')} · ${formatMoneyCompact(g.value)}`}
                    />
                    {rows.map((d) => {
                      const owner = d.repSlug ? SALES_REPS.find((r) => r.slug === d.repSlug) : undefined;
                      const days = d.daysSinceStageChange;
                      const sinceGoLive = days != null && days >= goLiveAge - 1;
                      return (
                        <Tr key={d.id}>
                          <Td className="min-w-0">
                            <p className="text-text-primary truncate max-w-[22rem]" title={d.name}>
                              {humanizeDealName(d.name)}
                            </p>
                            <div className="max-w-[22rem]">
                              <CompanySubLine name={d.companyName} dealName={d.name} />
                            </div>
                          </Td>
                          {ownerColumn && (
                          <Td align="center">
                            {usualOwner && ownerKey(d) === usualOwner ? null : owner ? (
                              <RepInitials rep={owner} />
                            ) : (
                              <span className="text-body-xs text-text-tertiary" title={d.ownerEmail ?? 'Unassigned'}>
                                {d.ownerEmail ? d.ownerEmail.split('@')[0] : <Nil />}
                              </span>
                            )}
                          </Td>
                          )}
                          <Td align="right" className="tabular-nums whitespace-nowrap text-text-secondary">
                            {days == null ? (
                              <Nil />
                            ) : (
                              sinceGoLive ? (
                                <span className="cursor-help text-body-xs text-text-muted" title={`${days} days: counted from the Attio go-live on 8 Jan 2026, so the deal has not moved since the import`}>
                                  since go-live
                                </span>
                              ) : (
                                days
                              )
                            )}
                          </Td>
                          <Td align="right" className="tabular-nums whitespace-nowrap text-text-tertiary">
                            {d.lastActivityAt ? (
                              <span title={`${d.daysSinceActivity ?? '?'} days ago`}>{formatShortDate(d.lastActivityAt)}</span>
                            ) : (
                              <span className="text-body-xs text-text-muted" title="No email or meeting with this account in Attio">
                                none
                              </span>
                            )}
                          </Td>
                          <Td align="right" className="text-text-primary tabular-nums whitespace-nowrap">
                            {formatMoneyCompact(d.valueEur)}
                          </Td>
                          <Td align="right" className="text-text-tertiary whitespace-nowrap !pr-3">
                            {!d.expectedCloseDate ? (
                              <Nil />
                            ) : isPlaceholderCloseDate(d.expectedCloseDate) ? (
                              <span className="text-text-tertiary/70" title="Attio's default close date (15 Jan 2027): never set">
                                default
                              </span>
                            ) : (
                              formatCloseDate(d.expectedCloseDate)
                            )}
                          </Td>
                          <Td align="right" className="!pr-0">
                            <AttioLink href={d.attioUrl} label={humanizeDealName(d.name)} />
                          </Td>
                        </Tr>
                      );
                    })}
                    {(hidden > 0 || (open && g.deals.length > GROUP_PREVIEW)) && (
                      <tr className="border-b border-border-light">
                        <td colSpan={cols} className="py-2">
                          <button
                            type="button"
                            className="text-body-xs text-text-tertiary hover:text-text-primary"
                            onClick={() => toggle(g.stage)}
                          >
                            {open ? 'Show fewer' : `Show ${hidden} more`}
                          </button>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </Table>
          <TableFooter>
            {plural(shown.length, 'stale deal')} · {formatMoneyCompact(shownValue)} · move, close or re-date them in Attio
          </TableFooter>
        </>
      )}
    </Card>
  );
}
